import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { fetchMetadataColumns, fetchStudiesMetadataColumns } from '@/helpers';
import InVivoError from '@/model/InVivoError.ts';
import type { MetadataItem } from '@/model/Metadata.model';
import type { Request } from '@/model/Request.model';
import { REQUEST_STATUS } from '@/model/Request.model';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { web as webRoute } from '@/support/route';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

export const filterOptions = [
  {
    name: 'Study',
    value: 'study',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: [],
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: [],
      },
    ],
    fetchFromApi: {
      placeholder: 'Search studies',
      url: 'studies.base',
    },
  },
];

export const generateColumns = async (signal: AbortSignal): Promise<Array<TanStackColumnDef<Request>>> => {
  let metadata: Array<MetadataItem> = [];
  try {
    metadata = await fetchStudiesMetadataColumns(signal);
  } catch (error) {
    if (notAborted(error)) {
      ExceptionHandler.captureException(
        new InVivoError('Could not fetch studies metadata', {
          cause: error,
          slug: 'request-load-metadata',
        })
      );
    }
  }

  // Sort was disabled on all columns in the original table, have kept that consistent for now.
  return [
    {
      id: 'requestName',
      header: 'Request name',
      accessorFn: (row) => row.request_template?.name,
      enableSorting: false,
      isVisible: true,
      cell: ({
        row: {
          original: { id, request_template },
        },
      }: {
        row: { original: Request };
      }) => (
        <Link
          to={webRoute('requests.details', {
            requestId: id,
          })}
          className="link blue"
        >
          {request_template?.name}
        </Link>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      enableSorting: false,
      isVisible: true,
      cell: ({
        row: {
          original: { status },
        },
      }: {
        row: { original: Request };
      }) => {
        return REQUEST_STATUS[status as keyof typeof REQUEST_STATUS];
      },
    },
    {
      id: 'assignee',
      header: 'Assignee',
      accessorFn: (row) => row.assigned_user?.name,
      enableSorting: false,
      isVisible: true,
      cell: ({ row: { original } }: { row: { original: Request } }) => original.assigned_user?.name ?? 'Unassigned',
    },
    {
      id: 'study',
      header: 'Study',
      accessorKey: 'study',
      enableSorting: false,
      isVisible: true,
      cell: ({
        row: {
          original: { study },
        },
      }: {
        row: { original: Request };
      }) => study?.name ?? '-',
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessorKey: 'created_at',
      enableSorting: false,
      isVisible: true,
      cell: ({ row: { original } }: { row: { original: Request } }) => <DateTimeRenderer value={original.created_at} />,
    },
    {
      id: 'dueDate',
      header: 'Due date',
      accessorKey: 'due_date',
      enableSorting: false,
      isVisible: true,
      cell: ({ row: { original } }: { row: { original: Request } }) => (
        <DateTimeRenderer value={original.due_date ?? original.form_data.due_date} defaultResponse="-" />
      ),
    },
    ...fetchMetadataColumns({ metadata, nestedStudyLocation: 'study' }),
  ];
};
